import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Badge, Button, CircularProgress, Fab, Theme, Tooltip, Typography, makeStyles } from "@material-ui/core"
import ChatMessage from "./ChatMessage"
import {
  chatApi,
  useGetMessagesQuery,
  useGetChatReadStatesQuery,
  useGetChatParticipantsQuery,
  useGetPinnedMessagesQuery,
  useLazyGetMessagePositionQuery,
  useMarkChatAsReadMutation,
} from "../api"
import { MessageViewModel } from "../api/message"
import { useChatMutations } from "../hooks/use-chat-mutations"
import EmojiPicker from "./EmojiPicker"
import PinnedMessagesBar from "./PinnedMessagesBar"
import MessageSearchPanel from "./MessageSearchPanel"
import { useAppContext } from "../../app-context/use-app-context"
import { socket } from "../../socket"
import { useAppDispatch } from "../../store"
import { makeReaders, readersOf } from "../utils/read-receipts"
import { unseenMentions } from "../utils/mentions"
import { ChatReadEvent } from "../api/chat-response"
import { AtSign } from "react-feather"
import { useTranslation } from "react-i18next"
import { getDayLabel, groupByDay } from "../utils/chatUtil"

const HIGHLIGHT_DURATION_MS = 1500
const PAGE_SIZE = 50
// Matches the backend's per-request cap.
const MAX_MESSAGES = 500
const LOAD_MORE_THRESHOLD_PX = 120
const AT_BOTTOM_THRESHOLD_PX = 80
const SEEN_MENTIONS_KEY = "chat:seen-mentions:"
const SEEN_MENTIONS_LIMIT = 50

// Per chat and per device, and only the tail: without it every visit would badge a mention you
// read last week as new, since the chat's read state says nothing about a single message.
const readSeenMentions = (chatId: string): string[] => {
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(SEEN_MENTIONS_KEY + chatId) ?? "[]")

    return Array.isArray(stored) ? stored : []
  } catch {
    return []
  }
}

const writeSeenMentions = (chatId: string, ids: string[]) => {
  try {
    window.localStorage.setItem(SEEN_MENTIONS_KEY + chatId, JSON.stringify(ids.slice(-SEEN_MENTIONS_LIMIT)))
  } catch {
    // Blocked or full storage: the badge still works, it just stops surviving a reload.
  }
}

type Props = {
  chatId: string
  searchOpen: boolean
  sentCount: number
  onCloseSearch: () => void
  onReply: (message: MessageViewModel) => void
}

const ChatMessages = ({ chatId, searchOpen, sentCount, onCloseSearch, onReply }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const [limit, setLimit] = useState(PAGE_SIZE)
  // Window's upper bound: `undefined` is the newest messages, an ISO timestamp the newest
  // older than it.
  const [before, setBefore] = useState<string>()
  const { data, currentData, isFetching, isError, refetch } = useGetMessagesQuery(
    { chatId, limit, before },
    { skip: !chatId, refetchOnMountOrArgChange: true }
  )

  const settled = useRef<{ chatId: string; limit: number; before?: string }>({ chatId, limit: PAGE_SIZE })

  if (currentData) settled.current = { chatId, limit, before }

  const isSameChat = settled.current.chatId === chatId
  const messages = useMemo(() => (isSameChat ? data ?? [] : []), [isSameChat, data])

  const settledLimit = settled.current.limit
  const hasMoreHistory = messages.length >= settledLimit && settledLimit < MAX_MESSAGES
  const reachedStartOfChat = messages.length > 0 && messages.length < settledLimit
  const loadingOlder = isFetching && !currentData && messages.length > 0
  const showInitialLoader = isFetching && messages.length === 0
  const { data: pinnedMessages = [] } = useGetPinnedMessagesQuery({ chatId }, { skip: !chatId })
  const { data: readStates = [] } = useGetChatReadStatesQuery({ chatId }, { skip: !chatId })
  const { data: participants = [] } = useGetChatParticipantsQuery({ chatId }, { skip: !chatId })

  // Empty while it loads, and empty for chat types with no explicit participant list — in both
  // cases nobody should be marked as having left.
  const memberIds = useMemo(() => new Set(participants.map(({ userId }) => userId)), [participants])

  // Grouped, not flat with separators between: sticky headers in one scrollport all pile up at the top.
  const dayGroups = useMemo(() => groupByDay(messages), [messages])

  const readers = useMemo(() => makeReaders(readStates), [readStates])
  const { appContext } = useAppContext()
  const { mainContextList } = appContext
  const currentUserId = mainContextList.find((ctx) => ctx.type === "user")?.id
  const containerRef = useRef<HTMLDivElement>(null)
  const pendingScrollHeight = useRef<number | null>(null)
  const dispatch = useAppDispatch()
  const [markChatAsRead] = useMarkChatAsReadMutation()
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)
  const highlightTimeout = useRef<number>()
  const [reactionTarget, setReactionTarget] = useState<{ messageId: string; anchorEl: HTMLElement } | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [pendingJumpId, setPendingJumpId] = useState<string | null>(null)
  const [fetchMessagePosition] = useLazyGetMessagePositionQuery()
  const [savingEdit, setSavingEdit] = useState(false)
  const { setMessageReaction, updateMessage, setMessageDeleted, setMessagePinned } = useChatMutations()

  const toggleReaction = (messageId: string, emoji: string, reacted: boolean) => {
    setMessageReaction({ chatId, messageId, emoji, reacted })
  }

  const submitEdit = async (messageId: string, content: string) => {
    try {
      setSavingEdit(true)

      // A failed edit keeps the editor open with the text still in it, so it can be retried.
      const updated = await updateMessage({ chatId, messageId, content })

      if (updated) setEditingMessageId(null)
    } finally {
      setSavingEdit(false)
    }
  }

  useEffect(() => {
    setEditingMessageId(null)
    setReactionTarget(null)
    setPendingJumpId(null)
    setLimit(PAGE_SIZE)
    setBefore(undefined)
  }, [chatId])

  const scrollToMessage = useCallback((messageId: string, behavior: ScrollBehavior = "auto") => {
    const container = containerRef.current
    const target = container?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`)

    if (!container || !target) return false

    // Centres the target in this container only. `scrollIntoView` would also scroll every
    // ancestor, and an animation started here lands at a stale offset if older messages
    // are prepended before it finishes — hence the caller's choice of behaviour.
    const targetRect = target.getBoundingClientRect()
    const offsetWithinContainer = targetRect.top - container.getBoundingClientRect().top
    const top = container.scrollTop + offsetWithinContainer - (container.clientHeight - targetRect.height) / 2

    container.scrollTo({ top, behavior })

    // A restore queued by a load in flight must not shift away from it afterwards.
    pendingScrollHeight.current = null

    setHighlightedMessageId(messageId)
    window.clearTimeout(highlightTimeout.current)
    highlightTimeout.current = window.setTimeout(() => setHighlightedMessageId(null), HIGHLIGHT_DURATION_MS)

    return true
  }, [])

  const jumpToMessage = useCallback(
    async (messageId: string) => {
      if (scrollToMessage(messageId, isFetching ? "auto" : "smooth")) return

      const located = await fetchMessagePosition({ chatId, messageId })
        .unwrap()
        .catch(() => null)

      if (!located) return

      const neededLimit = located.position + 1

      // `position` counts from the newest message, so widening aims wrong once anchored.
      // The extra millisecond keeps the target inside the exclusive `before`.
      if (before || neededLimit > MAX_MESSAGES) {
        setBefore(new Date(new Date(located.createdAt).getTime() + 1).toISOString())
        setLimit(PAGE_SIZE)
      } else {
        setLimit((current) => Math.max(current, neededLimit))
      }

      setPendingJumpId(messageId)
    },
    [chatId, fetchMessagePosition, scrollToMessage, isFetching, before]
  )

  useEffect(() => () => window.clearTimeout(highlightTimeout.current), [])

  const topSentinelRef = useRef<HTMLDivElement>(null)

  // A sentinel rather than a scroll threshold, so it also fires when the messages don't
  // fill the screen.
  useEffect(() => {
    const container = containerRef.current
    const sentinel = topSentinelRef.current

    if (!container || !sentinel || !hasMoreHistory || isFetching || pendingJumpId) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return

        // Older messages push everything down; this is what keeps the reading position.
        pendingScrollHeight.current = container.scrollHeight
        setLimit((current) => Math.min(Math.max(current, settled.current.limit + PAGE_SIZE), MAX_MESSAGES))
      },
      { root: container, rootMargin: `${LOAD_MORE_THRESHOLD_PX}px` }
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMoreHistory, isFetching, pendingJumpId])

  useLayoutEffect(() => {
    const container = containerRef.current

    if (!container || pendingScrollHeight.current === null) return

    container.scrollTop += container.scrollHeight - pendingScrollHeight.current
    pendingScrollHeight.current = null
  }, [messages])

  // After the restore, so a jump's explicit position wins over preserving the old one.
  useLayoutEffect(() => {
    if (!pendingJumpId) return

    if (scrollToMessage(pendingJumpId)) setPendingJumpId(null)
  }, [messages, pendingJumpId, scrollToMessage])

  const lastMessageId = messages[messages.length - 1]?.id
  const anchoredChatId = useRef<string | null>(null)
  const isAtBottom = useRef(true)

  const returnToLatest = useCallback(() => {
    setBefore(undefined)
    setLimit(PAGE_SIZE)
    setPendingJumpId(null)
    // Lets the bottom-anchor effect below run again; without it the view stays parked.
    anchoredChatId.current = null
  }, [])

  // Via a ref: depending on `before` directly would make setting an anchor undo it.
  const anchored = useRef(before)
  anchored.current = before

  // Unlike someone else's message arriving, a send should always land you on it.
  useEffect(() => {
    if (!sentCount) return

    if (anchored.current) {
      returnToLatest()
      return
    }

    // Instant: a smooth scroll's mid-flight events would flip this back off.
    isAtBottom.current = true
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight })
  }, [sentCount, returnToLatest])

  useEffect(() => {
    const container = containerRef.current

    if (!container) return

    const trackPosition = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight

      isAtBottom.current = distanceFromBottom <= AT_BOTTOM_THRESHOLD_PX
    }

    container.addEventListener("scroll", trackPosition, { passive: true })

    return () => container.removeEventListener("scroll", trackPosition)
  }, [])

  const [seenMentionIds, setSeenMentionIds] = useState<string[]>(() => readSeenMentions(chatId))

  useEffect(() => setSeenMentionIds(readSeenMentions(chatId)), [chatId])

  const pendingMentions = useMemo(
    () => unseenMentions(messages, currentUserId, seenMentionIds),
    [messages, currentUserId, seenMentionIds]
  )

  // Persisted from inside the update so it is written against the chat it belongs to — a
  // separate effect would run once more after a chat switch, under the new chat's id.
  const markMentionsSeen = useCallback(
    (ids: string[]) => {
      setSeenMentionIds((current) => {
        const next = current.concat(ids.filter((id) => !current.includes(id)))

        if (next.length === current.length) return current

        writeSeenMentions(chatId, next)

        return next
      })
    },
    [chatId]
  )

  // On screen is what clears a badge, whether you scrolled there yourself or clicked it.
  useEffect(() => {
    const container = containerRef.current

    if (!container || pendingMentions.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const seen = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target.getAttribute("data-message-id") ?? "")

        markMentionsSeen(seen.filter(Boolean))
      },
      { root: container }
    )

    pendingMentions.forEach(({ id }) => {
      const element = container.querySelector(`[data-message-id="${id}"]`)

      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [pendingMentions, markMentionsSeen])

  // Assigned before the browser paints, so opening a chat starts at the newest message
  // rather than visibly scrolling to it.
  useLayoutEffect(() => {
    const container = containerRef.current

    if (!container || !lastMessageId || anchoredChatId.current === chatId) return

    const jumpToBottom = () => {
      container.scrollTop = container.scrollHeight
    }

    anchoredChatId.current = chatId
    isAtBottom.current = true

    jumpToBottom()
    // Attachments and fonts can settle a frame later and change the height.
    requestAnimationFrame(jumpToBottom)
  }, [chatId, lastMessageId])

  // Following a new message would drag a reader away from the history they scrolled to.
  useLayoutEffect(() => {
    const container = containerRef.current

    if (!container || anchoredChatId.current !== chatId || !isAtBottom.current) return

    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" })
  }, [chatId, lastMessageId])

  // Join the chat room when chatId changes
  useEffect(() => {
    if (!chatId) return

    socket.emit("chat:join", chatId)

    return () => {
      socket.emit("chat:leave", chatId)
    }
  }, [chatId])

  // Mark the chat as read whenever it's opened. The backend never does this on
  // its own (opening a chat / fetching messages doesn't mark it read server-side).
  useEffect(() => {
    if (!chatId) return

    markChatAsRead({ chatId }).catch(() => {})
  }, [chatId, markChatAsRead])

  // In-place changes: unlike a new message these don't affect read state or the chat list.
  useEffect(() => {
    const handleMessageChange = () => {
      dispatch(chatApi.util.invalidateTags([{ type: "Message", id: chatId }]))
    }

    socket.on("message:reaction", handleMessageChange)
    socket.on("message:updated", handleMessageChange)

    return () => {
      socket.off("message:reaction", handleMessageChange)
      socket.off("message:updated", handleMessageChange)
    }
  }, [chatId, dispatch])

  // Someone caught up on this chat, so our own messages may now be read. The event already carries
  // the reader's whole watermark, so apply it rather than invalidating: a refetch here competes with
  // the ones a new message already triggers and can leave the read state stale.
  useEffect(() => {
    const handleChatRead = ({ chatId: readChatId, userId, userName, lastReadAt }: ChatReadEvent) => {
      if (readChatId !== chatId || !userId || userId === currentUserId) return

      dispatch(
        chatApi.util.updateQueryData("getChatReadStates", { chatId }, (readStates) => {
          const existing = readStates.find((readState) => readState.userId === userId)

          if (existing) existing.lastReadAt = lastReadAt
          else readStates.push({ userId, userName, lastReadAt })
        })
      )
    }

    socket.on("chat:read", handleChatRead)

    return () => {
      socket.off("chat:read", handleChatRead)
    }
  }, [chatId, currentUserId, dispatch])

  // Listen for new messages
  useEffect(() => {
    const handleMessage = () => {
      dispatch(
        chatApi.util.invalidateTags([
          {
            type: "Message",
            id: chatId,
          },
        ])
      )

      // A message arriving while this chat is open would otherwise re-appear as
      // unread (there's no live push for unread counts) — keep it marked read.
      markChatAsRead({ chatId }).catch(() => {})
    }

    socket.on("message:new", handleMessage)

    return () => {
      socket.off("message:new", handleMessage)
    }
  }, [chatId, dispatch, markChatAsRead])

  return (
    <div className={classes.wrapper}>
      {searchOpen && <MessageSearchPanel chatId={chatId} onJumpToMessage={jumpToMessage} onClose={onCloseSearch} />}

      <PinnedMessagesBar
        messages={pinnedMessages}
        onJumpToMessage={jumpToMessage}
        onUnpin={(messageId) => setMessagePinned({ chatId, messageId, pinned: false })}
      />

      <div className={classes.scrollArea}>
        {loadingOlder && (
          <div className={classes.loadingOlder}>
            <span className={classes.loadingOlderPill}>
              <CircularProgress size={16} />
            </span>
          </div>
        )}

        {pendingMentions.length > 0 && (
          <Tooltip title={t("chatMessages.mentionBadgeHint")}>
            <Badge badgeContent={pendingMentions.length} color="error" className={classes.mentionBadge}>
              <Fab
                size="small"
                color="primary"
                aria-label={t("chatMessages.jumpToMentionLabel")}
                onClick={() => jumpToMessage(pendingMentions[0].id)}
              >
                <AtSign size={18} />
              </Fab>
            </Badge>
          </Tooltip>
        )}

        {before && (
          <div className={classes.jumpToLatest}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.jumpToLatestButton}
              onClick={returnToLatest}
            >
              {t("chatMessages.jumpToLatestButtonLabel")}
            </Button>
          </div>
        )}

        <div ref={containerRef} className={classes.root}>
          {showInitialLoader && (
            <div className={classes.center}>
              <CircularProgress size={28} />
            </div>
          )}

          {/* Not sticky: it would never leave the viewport, so the observer would report it
            visible forever and keep loading. */}
          <div ref={topSentinelRef} className={classes.topSentinel} />

          {reachedStartOfChat && (
            <Typography className={classes.startOfChat} color="textSecondary">
              {t("chatMessages.startOfChatMessage")}
            </Typography>
          )}

          {isError && (
            <div className={classes.center}>
              <Typography color="error" gutterBottom>
                {t("chatMessages.loadErrorMessage")}
              </Typography>

              <Button variant="outlined" onClick={refetch}>
                {t("chatMessages.retryButtonLabel")}
              </Button>
            </div>
          )}

          {!showInitialLoader &&
            !isError &&
            dayGroups.map(({ day, messages: dayMessages }) => (
              <div key={day} className={classes.dayGroup}>
                <div className={classes.daySeparator}>
                  <span className={classes.dayLabel}>{getDayLabel(day, t)}</span>
                </div>

                {dayMessages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    currentUserId={currentUserId}
                    editing={editingMessageId === message.id}
                    savingEdit={savingEdit}
                    highlighted={highlightedMessageId === message.id}
                    onReply={() => onReply(message)}
                    onJumpToMessage={jumpToMessage}
                    onToggleReaction={(emoji, reacted) => toggleReaction(message.id, emoji, reacted)}
                    onAddReaction={(anchorEl) => setReactionTarget({ messageId: message.id, anchorEl })}
                    onTogglePin={() => setMessagePinned({ chatId, messageId: message.id, pinned: !message.pinnedAt })}
                    onSetDeleted={(deleted) => setMessageDeleted({ chatId, messageId: message.id, deleted })}
                    onStartEdit={() => setEditingMessageId(message.id)}
                    onCancelEdit={() => setEditingMessageId(null)}
                    onSubmitEdit={(content) => submitEdit(message.id, content)}
                    readBy={readersOf(readers, message.createdAt)}
                    senderLeft={message.type !== "system" && memberIds.size > 0 && !memberIds.has(message.senderId)}
                  />
                ))}
              </div>
            ))}

          <EmojiPicker
            anchorEl={reactionTarget?.anchorEl ?? null}
            onSelect={(emoji) => {
              if (reactionTarget) toggleReaction(reactionTarget.messageId, emoji, true)

              setReactionTarget(null)
            }}
            onClose={() => setReactionTarget(null)}
          />

          {!showInitialLoader && !isError && messages.length === 0 && (
            <div className={classes.center}>
              <Typography color="textSecondary">{t("chatMessages.emptyMessage")}</Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },

  root: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    background: theme.palette.grey[100],
  },
  topSentinel: {
    height: 1,
    flexShrink: 0,
  },

  // Bounds the sticky header to its own day.
  dayGroup: {
    flexShrink: 0,
  },

  daySeparator: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1, 0),
    pointerEvents: "none",
  },

  dayLabel: {
    padding: theme.spacing(0.25, 1.5),
    borderRadius: 999,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    color: theme.palette.text.secondary,
  },

  startOfChat: {
    flexShrink: 0,
    padding: theme.spacing(1),
    textAlign: "center",
    fontSize: 12,
  },

  scrollArea: {
    position: "relative",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },

  // Overlaid rather than in the list: in flow it would push every message down by its
  // own height the moment a load starts.
  loadingOlder: {
    position: "absolute",
    top: theme.spacing(1),
    left: 0,
    right: 0,
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
  },

  loadingOlderPill: {
    display: "flex",
    padding: theme.spacing(0.75),
    borderRadius: "50%",
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },

  mentionBadge: {
    position: "absolute",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 1,
  },

  jumpToLatest: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: 0,
    right: 0,
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
  },

  jumpToLatestButton: {
    pointerEvents: "auto",
    borderRadius: 999,
    boxShadow: theme.shadows[2],
  },

  center: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
}))

export default ChatMessages
